import { Component, OnDestroy, AfterContentInit } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LoadService } from 'src/app/services/application/load.service';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { AlertService } from 'src/lib/ariadna/alert';
import { DownloadTypes, ImgTypes } from './patient-files-types';

type TDocument = {
    dat_str: string;
    file_name: string;
    id: number;
    text?: string;
    dtSort: Date;
};

type TDocumentList = Array<TDocument>;

interface IDocumentResult {
    filebuffer: {
        data: Buffer;
    };
    filetype: string;
}
@Component({
    selector: 'patient-files',
    templateUrl: './patient-files.component.html',
    styleUrls: ['./patient-files.component.scss'],
})
export class PatientFilesComponent implements AfterContentInit, OnDestroy {
    public filesSection: boolean = false;
    loading: boolean = false;
    load_document: boolean = false;
    $documents: Subscription | undefined;
    documentsList: TDocumentList = [];
    documentListOnPage: TDocumentList = [];
    documentLimitOnPage: number = 8;
    selectedIndex: number | null = null;
    activeImgInBase64: string | null = null;
    downloadLink: string | null = null;
    fileNameWithExt: string | null = null;
    isMobile: boolean = false;
    public showRightMobile: boolean = false;
    public selectedItem: TDocument | null = null;

    constructor(
        private fileServices: FileUploadService,
        private load: LoadService,
        private router: Router,
        private alertService: AlertService
    ) {
        if (load.configData.menu.items.indexOf('documents') === -1) {
            this.router.navigateByUrl('/');
        } else {
            this.filesSection = true;
        }
    }

    private mobileMediaQuery!: MediaQueryList;

    ngOnInit(): void {
        this.initializeMobileCheck();
    }

    initializeMobileCheck(): void {
        // Определяем мобильное устройство по ширине экрана
        this.mobileMediaQuery = window.matchMedia('(max-width: 768px)');
        this.isMobile = this.mobileMediaQuery.matches;

        // Слушаем изменения размеров экрана
        this.mobileMediaQuery.addEventListener(
            'change',
            this.handleMediaChange
        );
    }

    handleMediaChange = (event: MediaQueryListEvent): void => {
        this.isMobile = event.matches;
    };

    destroyMobileCheck(): void {
        this.mobileMediaQuery.removeEventListener(
            'change',
            this.handleMediaChange
        );
    }

    ngAfterContentInit() {
        this.getDocumentsList();
    }

    ngOnDestroy(): void {
        this.destroyMobileCheck(); // Удаляем слушатель
        this.$documents?.unsubscribe();
    }
    getDocumentsList(): void {
        this.loading = true;
        this.fileServices
            .getDocumentsList()
            .pipe(finalize(() => (this.loading = false)))
            .subscribe(
                (value: TDocumentList) => {
                    // Используем parseDateString для точного парсинга
                    value.forEach((item) => {
                        const parsedDate = this.parseDateString(item.dat_str);
                        if (parsedDate) {
                            item.dtSort = parsedDate;
                        } else {
                            item.dtSort = new Date(); // или установите значение по умолчанию
                        }
                    });

                    // Сортируем по дате (по убыванию)
                    this.documentsList = value.sort(
                        (a, b) => b.dtSort.getTime() - a.dtSort.getTime()
                    );

                    // Обновляем список на странице
                    this.documentListOnPage = this.documentsList.slice(
                        0,
                        this.documentLimitOnPage
                    );
                },
                (err: Error) => {
                    this.alertService.error(
                        'Ошибка при получении списка документов'
                    );
                    console.error('Error in getDocumentsList', err);
                }
            );
    }
    async getDocument(
        $event: any,
        id: number,
        file_name: string,
        activeIndex: number
    ): Promise<void> {
        if (this.isMobile) {
            this.showRightMobile = true;
        }

        $event.stopPropagation();
        this.selectedIndex = activeIndex;
        this.selectedItem = this.documentsList[this.selectedIndex];

        this.load_document = true;
        this.downloadLink = null;
        this.activeImgInBase64 = null;
        this.fileNameWithExt = null;
        try {
            const result: IDocumentResult = await lastValueFrom(
                this.fileServices.getDocument(id, file_name)
            );
            // console.log('API Response:', result);
            // Добавьте небольшую задержку для визуализации лоадера
            await new Promise((resolve) => setTimeout(resolve, 300));
            if (this.isEnumValue(ImgTypes, result.filetype)) {
                await this.openImg(result.filebuffer.data, result.filetype);
            } else if (this.isEnumValue(DownloadTypes, result.filetype)) {
                this.createUrlForDownload(
                    result.filebuffer.data,
                    result.filetype
                );
            } else {
                throw new Error('Неизвестный тип документа');
            }

            this.fileNameWithExt = file_name.toLowerCase();
        } catch (e) {
            console.error(e);
            this.alertService.error(
                `${
                    e instanceof Error ? e.message : 'Ошибка загрузки документа'
                }`
            );
        } finally {
            this.load_document = false;
        }
    }

    async _arrayBufferToBase64(buffer: Buffer): Promise<string> {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }
    async openImg(filebuffer: Buffer, filetype: string): Promise<void> {
        this.activeImgInBase64 = `data:image/${filetype};base64,${await this._arrayBufferToBase64(
            filebuffer
        )}`;

        this.createUrlForDownload(filebuffer, filetype);
    }
    isEnumValue(enumObj: Record<string, string>, value: string): boolean {
        return Object.values(enumObj).includes(value.toLowerCase());
    }
    createUrlForDownload(filebuffer: Buffer, filetype: string): void {
        try {
            const bytes = new Uint8Array(filebuffer);
            const blob = new Blob([bytes], {
                type: filetype,
            });
            const url = window.URL.createObjectURL(blob);
            this.downloadLink = url;
        } catch (e) {
            throw e;
        }
    }

    protected changedPage(event: any) {
        this.documentListOnPage = this.documentsList.slice(
            0,
            this.documentLimitOnPage
        );

        const start: number = this.documentLimitOnPage * event;
        const end: number = start + this.documentLimitOnPage;
        this.documentListOnPage = this.documentsList.slice(start, end);
    }

    // Вспомогательный метод для парсинга строки даты
    private parseDateString(dateStr: string): Date | null {
        try {
            const [datePart, timePart] = dateStr.split(' ');
            const [day, month, year] = datePart.split('.').map(Number);
            const [hours, minutes] = timePart.split(':').map(Number);
            return new Date(year, month - 1, day, hours, minutes); // Месяцы в JS с 0
        } catch (e) {
            console.error('Ошибка парсинга даты:', e);
            return null;
        }
    }

    // Упрощенная версия методов
    isBlockYear(index: number): boolean {
        if (index === 0) return true;

        return (
            this.documentsList.length >= index + 1 &&
            this.documentsList[index].dtSort.getFullYear() !==
                this.documentsList[index - 1].dtSort.getFullYear()
        );
    }

    getYear(index: number): string {
        if (this.documentsList && this.documentsList[index]) {
            return this.documentsList[index].dtSort.getFullYear() + '';
        } else {
            return '';
        }
    }

    getYearPC(index: number): string {
        const dateStr = this.documentsList[index]?.dat_str;
        if (!dateStr) return '';
        const parsedDate = this.parseDateString(dateStr);
        return parsedDate?.getFullYear().toString() || '';
    }

    getFileFirmat(filename: string): string {
        const cleanedFilename = filename.trim();
        const lastDotIndex = cleanedFilename.lastIndexOf('.');
        if (lastDotIndex <= 0 || lastDotIndex === cleanedFilename.length - 1) {
            return '';
        }
        return cleanedFilename.substring(lastDotIndex + 1).toLowerCase();
    }

    async downloadFile(
        $event: any,
        id: number,
        file_name: string,
        activeIndex: number
    ): Promise<void> {
        $event.stopPropagation(); // Prevent event bubbling

        this.load_document = true;

        try {
            const result: IDocumentResult = await lastValueFrom(
                this.fileServices.getDocument(id, file_name)
            );

            // Convert buffer to Blob and trigger download
            const bytes = new Uint8Array(result.filebuffer.data);
            const blob = new Blob([bytes], { type: result.filetype });
            const url = window.URL.createObjectURL(blob);

            // Create temporary <a> element for download
            const link = document.createElement('a');
            link.href = url;
            link.download = file_name.toLowerCase();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url); // Clean up

            this.alertService.success('Документ успешно скачан');
        } catch (e) {
            console.error(e);
            this.alertService.error(
                e instanceof Error ? e.message : 'Ошибка скачивания'
            );
        } finally {
            this.load_document = false;
        }
    }

    handleClose() {
        this.selectedIndex = null; // Сбрасываем выбранный индекс
        this.downloadLink = null; // Очищаем ссылку на скачивание
        this.activeImgInBase64 = null; // Очищаем изображение
    }
}
